import type { ExplorerColumn, ExplorerColumnType, ExplorerRow } from '../types/explorer.types'

/**
 * Cell formatting and row grouping for the Explorer grid (MYD-360).
 *
 * Deliberately dependency-free, matching `utils/tasks.ts`: no date library, so
 * it stays cheap to import anywhere and its behaviour is entirely visible here.
 */

/** What an empty cell renders as. One character, so a sparse column stays scannable. */
export const EMPTY_CELL = '-'

/**
 * Format one cell by its catalog type.
 *
 * Every branch has to survive the value being null, a string where a number was
 * expected, or a date the server could not parse. A report is the surface most
 * likely to meet a row nobody has looked at in years, so a single bad value
 * must render as a dash rather than throwing and blanking the whole grid.
 */
export function formatCell(value: unknown, type: ExplorerColumnType, locale = 'en-US'): string {
	if (value === null || value === undefined || value === '') return EMPTY_CELL

	switch (type) {
		case 'currency': {
			const amount = typeof value === 'number' ? value : Number(value)
			if (!Number.isFinite(amount)) return String(value)
			return amount.toLocaleString(locale, {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			})
		}

		case 'number': {
			const amount = typeof value === 'number' ? value : Number(value)
			// A non-numeric value in a number column means the data is dirty, not
			// that the cell is empty. Showing it raw is more useful than a dash,
			// which would hide the problem.
			return Number.isFinite(amount) ? amount.toLocaleString(locale) : String(value)
		}

		case 'date': {
			const date = value instanceof Date ? value : new Date(String(value))
			if (Number.isNaN(date.getTime())) return String(value)
			// Rendered in UTC. The server treats date filters as UTC days, so
			// formatting in local time would show a row as the 2nd that a filter
			// "from the 3rd" had just matched.
			return date.toLocaleDateString(locale, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				timeZone: 'UTC',
			})
		}

		case 'boolean':
			return value ? 'Yes' : 'No'

		case 'text':
		case 'enum':
		default:
			return String(value)
	}
}

/** Numeric columns are right-aligned so digits line up down the column. */
export function isNumericType(type: ExplorerColumnType): boolean {
	return type === 'number' || type === 'currency'
}

export interface ExplorerRowGroup {
	/** Stable key for the group. Empty values collapse to one sentinel group. */
	key: string
	/** The raw value, so the header can render through the same cell formatter. */
	value: unknown
	rows: ExplorerRow[]
}

/** Sentinel for rows whose group value is missing, so they form one group. */
export const EMPTY_GROUP_KEY = '__empty__'

/**
 * Contiguous groups over the rows of one page.
 *
 * Grouping is a page-local view: the server has already ordered rows by the
 * group key as a leading sort, so adjacent equal values form a group. That is
 * why this walks the array rather than bucketing by value. Bucketing would also
 * reorder rows within a group and quietly break the user's own sort.
 *
 * Returns null when nothing is grouped, so the caller renders a plain table.
 */
export function groupRows(rows: ExplorerRow[], groupKey: string | null): ExplorerRowGroup[] | null {
	if (!groupKey) return null

	const groups: ExplorerRowGroup[] = []

	for (const row of rows) {
		const raw = row[groupKey]
		const key = raw === null || raw === undefined || raw === '' ? EMPTY_GROUP_KEY : String(raw)
		const last = groups[groups.length - 1]

		if (last && last.key === key) last.rows.push(row)
		else groups.push({ key, value: raw, rows: [row] })
	}

	return groups
}

/**
 * The group keys on this page, in display order.
 *
 * Defined through `groupRows` rather than repeating the contiguous walk, so
 * the collapse-all control can never disagree with the headers the grid
 * actually drew.
 */
export function groupKeysOf(rows: ExplorerRow[], groupKey: string | null): string[] {
	return groupRows(rows, groupKey)?.map(group => group.key) ?? []
}

/**
 * The header label for a column key.
 *
 * Falls back to the key itself rather than rendering an empty header. A saved
 * report can outlive a column: if one is removed from the catalog, the user
 * should see which key no longer resolves instead of a blank column.
 */
export function columnLabel(column: ExplorerColumn | undefined, key: string): string {
	return column?.label ?? key
}

/** "1 location" / "24 locations", using the grain's own word for a row. */
export function rowCountLabel(total: number, rowLabel: string): string {
	return `${total.toLocaleString()} ${rowLabel}${total === 1 ? '' : 's'}`
}
